"use no memo";

/* eslint-disable import/prefer-default-export, complexity */
/// <reference types="google.maps" />

import { forEach } from 'lodash-es';

import { COUNTRIES } from 'helpers/constants';
import { GoogleMapsType } from 'types/gMaps';

type Place = google.maps.places.Place;

/**
 * Legacy address shape originally returned by `@ubilabs/react-geosuggest`.
 *
 * Preserved as the de facto domain model for addresses across this app:
 * - persisted to `sessionStorage` via `helpers/customer.js`
 * - threaded through Redux/Context state
 * - matches the on-the-wire ChowNow backend API contract (`street_address1`,
 *   `street_address2`, `city`, `state`, `zip`, etc.)
 *
 * `placeToLegacyShape` adapts modern `google.maps.places.Place` results into
 * this shape so the ~20+ downstream consumers remain unchanged. See the
 * address translation layer research doc in memory for the full migration story.
 */
export type SelectedAddress = {
  gmaps: {
    address_components: Array<{
      long_name: string;
      short_name: string;
      types: string[];
    }>;
    formatted_address: string;
    geometry: {
      location: { lat: () => number; lng: () => number };
    };
    place_id: string;
    types: string[];
  };
};

export function placeToLegacyShape(place: Place): SelectedAddress {
  const components: SelectedAddress['gmaps']['address_components'] = (
    place.addressComponents || []
  ).map((c) => ({
    long_name: c.longText || '',
    short_name: c.shortText || '',
    types: c.types || [],
  }));

  // Hoisting `place.location` to a local const lets the arrow functions below
  // close over a narrowed `LatLng` value without needing a non-null assertion.
  const loc = place.location;
  // The (0, 0) fallback is defensive — Place Details API reliably returns
  // `location` when 'location' is in PLACE_FIELDS, so this branch should not
  // fire in practice. Note: this differs from `react-geosuggest`, which left
  // location undefined when missing.
  const location = loc
    ? { lat: () => loc.lat(), lng: () => loc.lng() }
    : { lat: () => 0, lng: () => 0 };

  return {
    gmaps: {
      address_components: components,
      formatted_address: place.formattedAddress || '',
      geometry: { location },
      place_id: place.id || '',
      types: place.types || [],
    },
  };
}

function extractGoogleMapComponents(
  addressComponents: {
    long_name: string;
    short_name: string;
    types: string[];
  }[],
  formattedAddress: string
): {
  street_address1?: string;
} {
  // Split the address components into an object keyed by type
  // ie, route: { short_name: ..., long_name: ... }
  const googleComponents = addressComponents.reduce(
    (acc: { [key: string]: object }, component) => {
      forEach(component.types, (type: string) => {
        acc[type] = component;
      });

      return acc;
    },
    {}
  );

  // Given a CN key, define how to grab it from the above `googleComponents` object
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const addressKeySelector: { [key: string]: any } = {
    street_address1: ({
      street_number: streetNumber = {},
      route = {},
    }: {
      street_number: { short_name?: string };
      route: { short_name?: string; long_name?: string };
    }) => {
      // Ensure closest match possible to formatted address; default to long_name if no match on short name
      const selectedRoute = formattedAddress.includes(route.short_name || '')
        ? route.short_name
        : route.long_name;
      // Quirk: when both `route.short_name` and `route.long_name` are absent
      // (e.g. plus-code-only results), the template literal stringifies
      // `undefined` and returns the literal string `"undefined"`. A guard
      // downstream (`=== 'undefined'`) and parallel checks in `order.js`,
      // `validation.ts`, and `UpdateFulfillmentDetailsButton.tsx` catch this
      // and substitute the plus_code. Do not "fix" this without updating all
      // consumers — the quirky string is the load-bearing sentinel.
      return `${streetNumber.short_name || ''} ${selectedRoute}`.trim();
    },
    street_address2: ({
      subpremise = {},
    }: {
      subpremise: { short_name?: string };
    }) => subpremise.short_name || null,
    country_alpha2: ({ country = {} }: { country: { short_name?: string } }) =>
      country.short_name,
    // eslint-disable-next-line consistent-return
    country: ({ country = {} }: { country: { short_name?: string } }) => {
      if (country.short_name === COUNTRIES.usa.alpha2) {
        return COUNTRIES.usa.alpha3;
      }
      if (country.short_name === COUNTRIES.canada.alpha2) {
        return COUNTRIES.canada.alpha3;
      }
    },
    zip: ({
      postal_code: zip = {},
    }: {
      postal_code: { short_name?: string };
    }) => zip.short_name,
    city: ({
      locality,
      sublocality,
    }: {
      locality: { long_name?: string };
      sublocality: { long_name?: string };
    }) => {
      // preferred location of key; it can sometimes be under sublocality
      const googleKey = locality || sublocality || {};
      if (googleKey.long_name) {
        return googleKey.long_name;
      }
      const addrParts = formattedAddress.split(',');
      return addrParts[addrParts.length - 3];
    },
    state: ({
      administrative_area_level_1: state = {},
    }: {
      administrative_area_level_1: { short_name?: string };
    }) => state.short_name,
  };

  // Reduce over the keys we want to return to form an object comprised of the values we need
  return Object.keys(addressKeySelector).reduce(
    (acc: { [key: string]: object }, key) => {
      acc[key] = addressKeySelector[key](googleComponents);
      return acc;
    },
    {}
  );
}

function extractLatLng(gmapsAddress: {
  geometry: {
    location: {
      lat: () => string;
      lng: () => string;
    };
  };
  latitude: string;
  longitude: string;
}): { latitude: string; longitude: string } {
  if (gmapsAddress.geometry) {
    return {
      latitude:
        typeof gmapsAddress.geometry.location.lat === 'function'
          ? gmapsAddress.geometry.location.lat()
          : gmapsAddress.geometry.location.lat,
      longitude:
        typeof gmapsAddress.geometry.location.lng === 'function'
          ? gmapsAddress.geometry.location.lng()
          : gmapsAddress.geometry.location.lng,
    };
  }
  return {
    latitude: gmapsAddress.latitude,
    longitude: gmapsAddress.longitude,
  };
}

export function deserializeAddress(address: GoogleMapsType) {
  const gmapsAddress = address.gmaps || address;
  const addressComponents = gmapsAddress.address_components || [];

  if (!gmapsAddress.address_components) return address;

  let gmapsComponents = extractGoogleMapComponents(
    addressComponents,
    gmapsAddress.formatted_address
  );

  // If we don't have a street address but do have a plus code, use plus code
  // for the street address. The `=== 'undefined'` (string!) check matches the
  // sentinel produced by `extractGoogleMapComponents` — see comment there.
  // Plus codes reference: https://maps.google.com/pluscodes/
  if (
    gmapsComponents.street_address1 === 'undefined' &&
    gmapsAddress.types.includes('plus_code')
  ) {
    const plusCode = addressComponents.find((component) =>
      component.types.includes('plus_code')
    );
    gmapsComponents = {
      ...gmapsComponents,
      street_address1: plusCode?.long_name,
    };
  }

  return {
    formatted_address:
      gmapsAddress.place_formatted_address || gmapsAddress.formatted_address,
    types: gmapsAddress.types || [],
    ...extractLatLng(gmapsAddress),
    ...gmapsComponents,
  };
}
